/**
 * THE SEAM TO THE AUTHORED MASTER (§10) — the interface, stated here, with the
 * implementation deliberately absent.
 *
 * ── Why this file has no loader in it ──────────────────────────────────────
 *
 * A parallel agent owns the production skeleton contract and the clip manifest
 * on `integration/skeleton-contract-final`. That branch has already frozen the
 * 74-bone table (`authoredSkeletonContract.ts`), the shared table checker with
 * per-rig tolerances (`skeletonValidation.ts`) and the swap analysis
 * (`skeletonSwap.ts`, including `SWAP_BLOCKERS` and an explicit
 * `RUNTIME_RETARGET_POLICY` that REFUSES a runtime remap). Writing a second
 * loader here would produce two answers to "is this asset conformant", and the
 * one that is wrong is the one nobody is looking at.
 *
 * So this module declares WHAT the runtime wire requires of that work and what
 * it must never accept, in a form the tests can assert today, and
 * `AUTHORED_MASTER_PROVIDER.build` stays unimplemented until the contract branch
 * lands. Everything upstream — the mode switch, the resolver, the contract
 * check, the animation director, the clip classification — already exists and is
 * exercised, so the integration is one `build` and a provider flag.
 *
 * ── What the production model IS ───────────────────────────────────────────
 *
 * FootyPlayer, the authored master. Measured off the runtime export report, not
 * remembered: 74 bones, root `root`, metres, 1.8039 m crown-to-sole, sole at
 * 0.0, ONE mesh, ONE primitive, ONE material, ONE atlas, max 4 skin influences,
 * alphaMode MASK. LOD1 (`player-master-lod1.glb`, 18 527 tris) is the gameplay
 * LOD — the camera sits at 17–20 m and LOD1 covers 6–18 m.
 *
 * ── What must NEVER be accepted, and why each one is a real trap ───────────
 *
 * Every item below has actually happened to this project or is one edit away.
 */

/** The production model's identity, as facts a check can be written against. */
export const AUTHORED_MASTER_REQUIREMENTS = {
  /** Bone count. The procedural test rig has 65 — the two share ZERO bone names. */
  boneCount: 74,
  /** Root bone. Not `mixamorig5Hips`; a different rig entirely. */
  rootBone: 'root',
  units: 'm',
  /** Crown-to-sole, metres, from optimized_report.json. */
  heightM: 1.8039,
  /** The MERGED, optimised runtime export — never the 19-material authoring file. */
  skeletonAsset: 'player-master-lod1.glb',
  meshCount: 1,
  materialCount: 1,
  maxInfluences: 4,
  atlas: 'player-master-atlas.png',
} as const;

/**
 * Assets that must never be attached to FootyPlayer, each with the failure it
 * produces. These are REFUSALS, not preferences.
 */
export const AUTHORED_MASTER_FORBIDDEN: ReadonlyArray<{
  readonly asset: string;
  readonly why: string;
}> = [
  {
    asset: 'mixamo-skeleton-animations.glb',
    why: 'the 65-bone TEST rig\'s clip set. It shares zero bone names with the master, '
      + 'so all 2,535 tracks resolve to nothing and the player stands in bind pose while '
      + 'every metric still reads healthy.',
  },
  {
    asset: 'Footy_Arena_Player_LOD0.glb',
    why: 'the AUTHORING file: 13 meshes, 19 materials. It is the source artefact, not a '
      + 'runtime asset — the contract requires one mesh and one material, and the merged '
      + 'export (player-master-lod*.glb) is what satisfies that.',
  },
  {
    asset: 'FootballPack_V1.glb',
    why: 'a THIRD rig on pre-rename UE naming. ~72% of tracks resolve, which is the worst '
      + 'possible outcome: arms and legs animate while pelvis, spine_03, neck_01, Head and '
      + 'both ball_* joints freeze in bind, so it looks like an animation bug forever.',
  },
  {
    asset: 'UAL1_Standard.glb',
    why: 'another foreign rig, same partial-resolution trap.',
  },
];

/**
 * NO RUNTIME RETARGETER. EVER.
 *
 * The clip pack (`characters/football/mocap/M_*.glb`) is already retargeted onto
 * the master OFFLINE — 222 of 222 tracks land and no clip carries baked travel.
 * A runtime bone remap would be a second, slower, less inspectable answer to a
 * problem that is already solved, and it is the thing that makes a partial
 * resolution (see FootballPack_V1 above) silently survivable instead of loud.
 * `skeletonSwap.ts` on the contract branch states the same policy in
 * `RUNTIME_RETARGET_POLICY`; this constant exists so a check in THIS package can
 * assert it without importing across the seam before it lands.
 */
export const RUNTIME_RETARGET_ALLOWED = false;

/**
 * ⚠ KEY ON THE FILE STEM, NEVER ON `AnimationClip.name`.
 *
 * Every one of the twelve `M_*.glb` clips is named `"Animation"` internally.
 * Keying a clip map on the clip's own name collapses all twelve onto one entry,
 * and the symptom is "only one animation ever plays" — which reads as a
 * controller bug and is not one.
 */
export const AUTHORED_CLIP_KEY = 'file-stem' as const;

/**
 * Why `authored-master` cannot build in THIS tree, in words a reader can act on.
 *
 * Stated as a list of prerequisites rather than "not done yet", because the
 * previous version of this string ("Track B owns it") outlived Track B and told
 * the next reader nothing.
 */
export const AUTHORED_MASTER_SEAM = {
  unavailableReason:
    'the authored FootyPlayer master is not wired in this tree yet. It needs, in order: '
    + '(1) the merged runtime export player-master-lod1.glb + player-master-atlas.png '
    + 'committed under packages/client/public/characters/player-master/ — they exist on '
    + 'disk but the copies there are a LATER re-optimisation than the frozen contract, so '
    + 'the contract must be re-frozen against whatever is committed; '
    + '(2) the 74-bone contract from integration/skeleton-contract-final '
    + '(AUTHORED_CHARACTER_CONTRACT / AUTHORED_FROZEN_BONES / checkSkeletonTable); '
    + '(3) the M_*.glb clip pack loaded BY FILE STEM (all twelve are internally named '
    + '"Animation"); (4) AUTHORED_MASTER_PROVIDER.build implemented against them and '
    + 'available set true. No runtime retargeter is needed or permitted — the pack is '
    + 'already retargeted offline, 222/222 tracks, zero baked travel.',
} as const;
